import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { ArrowUpDown, CalendarDays, Check, ChevronDown, RefreshCw, X } from 'lucide-react';
import { CLIENT_MENU_TABS, CLIENT_TAB_ROW } from '../clientListView';
import { RECRUIT_SORTS, RECRUIT_TABS } from '../recruitListView';
import type { RecruitSort, RecruitTab } from '../recruitListView';
import { LEGACY_STATE_LABEL_KEYS } from '../clientLegacyState';
import type { ClientSort, ClientTab } from '../clientListView';
import type { ClientLegacyState } from '../clientLegacyState';
import type { IntlLike } from '../clientPresentation';

// Tab row from the old CustomFilterSwitch (handoff PART 6j): 全部 / 跟進 / 潛力 /
// 提醒 / 招募 + a sort control + 新增客戶. 家庭 is still a tab but lives in the
// menu (CLIENT_TAB_ROW), and the 自定 placeholder is gone — see clientListView.
const TAB_LABEL: Record<ClientTab, string> = {
  all: 'clientv2-tab-all',
  follow: 'clientv2-tab-follow',
  potential: 'clientv2-tab-potential',
  family: 'clientv2-tab-family',
  reminder: 'clientv2-tab-reminder',
  recruit: 'clientv2-tab-recruit'
};

// 招募模式's own row. 進行中 是 初步接觸 + 洽談中 —— the two stages actually
// being worked; 未開始 and 待評估 are tiles instead, so no idea gets both a tab
// and a tile. 全部 counts every candidate, 暫緩 included: an adviser asking for
// 全部 means all of them, and a parked candidate hidden from the total is the
// graveyard problem wearing a different hat.
const RECRUIT_TAB_LABEL: Record<RecruitTab, string> = {
  all: 'clientv2-tab-all',
  active: 'clientv2-recruit-tab-active',
  exam: 'clientv2-recruit-stage-exam',
  paused: 'clientv2-recruit-stage-paused'
};

export const RECRUIT_SORT_LABEL: Record<RecruitSort, string> = {
  default: 'clientv2-sort-default',
  score: 'clientv2-recruit-sort-score',
  quiz: 'clientv2-recruit-sort-quiz',
  unrated: 'clientv2-recruit-sort-unrated'
};

// Derived, so the menu and the trigger label cannot name one option two ways.
export const RECRUIT_SORT_OPTIONS: Array<{ value: RecruitSort; labelKey: string }> = RECRUIT_SORTS.map(value => ({
  value,
  labelKey: RECRUIT_SORT_LABEL[value]
}));

const SORT_LABEL: Record<ClientSort, string> = {
  // The server's own newest-first order. V1 spelled this 「All」 because its
  // clientState '' was both the unsorted list AND the unfiltered one; here the
  // 全部 tab chip already says 「unfiltered」, so re-using that word in the menu
  // put the same label on two controls that do different things. This row is
  // now only about the ORDER — which is why it reads 「新→舊」 rather than
  // 「最新加入」: the other two sorts already name a direction (潛力高→低, 姓名
  // A–Z), and a label that names only one end of the range leaves the reader to
  // guess where the list runs to. No dimension word in front of the arrow: this
  // is the list's default order, so 新 and 舊 have nothing to be about except
  // when the client arrived.
  default: 'clientv2-sort-default',
  potential: 'clientv2-sort-potential',
  name: 'clientv2-sort-name'
};

/**
 * Copy of V1's `clientState` <select> (clientPage.js:2173-2330), which is one
 * control mixing list states and sorts. Order and labels are V1's, except the
 * two sorts keep the v46 wording already translated as `clientv2-sort-*`.
 *
 * `always: false` reproduces V1's `hide-option` entries: states you can only
 * arrive at from elsewhere (a notification, a dashboard tile, AdviserBlast) are
 * listed only while they are the active state, so the control can always show
 * what is currently applied.
 */
type MenuOption =
  | { kind: 'sort'; value: ClientSort; labelKey: string; always: boolean }
  | { kind: 'tab'; value: ClientTab; labelKey: string; always: boolean }
  | { kind: 'state'; value: ClientLegacyState; labelKey: string; always: boolean };

const MENU_OPTIONS: MenuOption[] = [
  // V1's first row was 「All」, which reset the list state, the sort AND the tab
  // in one go — the same word the 全部 chip carries, on a control that did more
  // than the chip does. It is a sort row now: it restores the server's
  // newest-first order and leaves the chosen tab alone. Clearing a list state is
  // the banner's 清除 button, and 全部 is one chip away.
  { kind: 'sort', value: 'default', labelKey: SORT_LABEL.default, always: true },
  // 家庭 moved off the chip row into here (CLIENT_TAB_ROW). Same predicate, same
  // callback — only the affordance changed.
  { kind: 'tab', value: 'family', labelKey: 'clientv2-tab-family', always: true },
  // Two of V1's rows are dropped here rather than reproduced, because the v46
  // tab row genuinely offers the same thing and listing them twice in the same
  // control just splits one action across two places:
  //   - 潛在招募  -> the 招募 tab (TAB_PREDICATE.recruit IS isRecruitmentOf)
  //   - 提醒我—所有 -> the 提醒 tab (identical predicate and date order)
  // 提醒我—所有 stays as a hide-option because a notification or top-nav deep
  // link can still put the page into that state, and the control has to be able
  // to name what is currently applied. 潛在招募 is reachable only from this
  // menu, so it simply goes.
  //
  // 潛力高→低 is NOT one of them and must stay: the 潛力 tab is a FILTER
  // (TAB_PREDICATE.potential keeps only scoreLevel === 0), whereas this sorts
  // the whole list by CPI band so medium/low clients move down rather than
  // disappear. Dropping it left `sort === 'potential'` unreachable and
  // sortClients' potential branch dead.
  { kind: 'state', value: 'allReminder', labelKey: LEGACY_STATE_LABEL_KEYS.allReminder, always: false },
  { kind: 'sort', value: 'potential', labelKey: SORT_LABEL.potential, always: true },
  { kind: 'sort', value: 'name', labelKey: SORT_LABEL.name, always: true },
  { kind: 'state', value: 'Inactive', labelKey: LEGACY_STATE_LABEL_KEYS.Inactive, always: true },
  { kind: 'state', value: 'Pending', labelKey: LEGACY_STATE_LABEL_KEYS.Pending, always: true },
  { kind: 'state', value: 'Connected', labelKey: LEGACY_STATE_LABEL_KEYS.Connected, always: true },
  { kind: 'state', value: 'Disconnected', labelKey: LEGACY_STATE_LABEL_KEYS.Disconnected, always: true },
  { kind: 'state', value: 'longPendingCases', labelKey: LEGACY_STATE_LABEL_KEYS.longPendingCases, always: true },
  { kind: 'state', value: 'imported', labelKey: LEGACY_STATE_LABEL_KEYS.imported, always: true },
  // V1 hide-option entries — visible only while active.
  { kind: 'state', value: 'lucky', labelKey: LEGACY_STATE_LABEL_KEYS.lucky, always: false },
  { kind: 'state', value: 'TrackOn', labelKey: LEGACY_STATE_LABEL_KEYS.TrackOn, always: false },
  {
    kind: 'state',
    value: 'Long time no see',
    labelKey: LEGACY_STATE_LABEL_KEYS['Long time no see'],
    always: false
  },
  { kind: 'state', value: 'remindMe', labelKey: LEGACY_STATE_LABEL_KEYS.remindMe, always: false },
  {
    kind: 'state',
    value: 'dateBackBirthdayCount',
    labelKey: LEGACY_STATE_LABEL_KEYS.dateBackBirthdayCount,
    always: false
  },
  { kind: 'state', value: 'particular', labelKey: LEGACY_STATE_LABEL_KEYS.particular, always: false },
  {
    kind: 'state',
    value: 'TodayAndTomorrowBirthday',
    labelKey: LEGACY_STATE_LABEL_KEYS.TodayAndTomorrowBirthday,
    always: false
  },
  { kind: 'state', value: 'ClientToSend', labelKey: LEGACY_STATE_LABEL_KEYS.ClientToSend, always: false },
  { kind: 'state', value: 'life', labelKey: LEGACY_STATE_LABEL_KEYS.life, always: false },
  { kind: 'state', value: 'illness', labelKey: LEGACY_STATE_LABEL_KEYS.illness, always: false },
  { kind: 'state', value: 'medical', labelKey: LEGACY_STATE_LABEL_KEYS.medical, always: false },
  { kind: 'state', value: 'saving', labelKey: LEGACY_STATE_LABEL_KEYS.saving, always: false },
  { kind: 'state', value: 'ilas', labelKey: LEGACY_STATE_LABEL_KEYS.ilas, always: false }
];

const isMenuTab = (tab: ClientTab): boolean => (CLIENT_MENU_TABS as readonly string[]).includes(tab);

export type ClientTabsProps = {
  activeTab: ClientTab;
  onTab: (tab: ClientTab) => void;
  counts: Partial<Record<ClientTab, number>>;
  sort: ClientSort;
  onSort: (sort: ClientSort) => void;
  /** Active notification/deep-link state, so the menu can show what is applied. */
  legacyState?: ClientLegacyState | null;
  /** null clears the state (V1's "All"). */
  onLegacyState?: (state: ClientLegacyState | null) => void;
  /** Explicit full-list refresh; the page no longer refetches on entry. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /**
   * The 📅 button, on every tab. It opens the reminder calendar — which lives on
   * the 提醒 tab, so pressing it from another tab moves the chip highlight
   * there too, and nobody reads the grid as that other tab's clients. Pressed
   * again, it goes back to the list. Rendered only when `onCalendar` is given.
   */
  calendarOpen?: boolean;
  onCalendar?: () => void;
  /**
   * 招募模式. When on, this component renders the recruitment row instead of the
   * business one — see the early return below for why it replaces rather than
   * extends. The three props travel together; without all of them the row would
   * have no way back to 業務, so the business row stays.
   */
  recruitMode?: boolean;
  /**
   * 這位顧問手上有招募模式的權限(FEATURE_RECRUITMODE)。只影響業務列上那顆
   * 「招募」分頁要不要穿招募的琥珀色:對沒有權限的人,它只是一個普通篩選,塗成
   * 另一個模式的顏色是在指一道不存在的門。
   */
  recruitAvailable?: boolean;
  recruitTab?: RecruitTab;
  onRecruitTab?: (tab: RecruitTab) => void;
  recruitCounts?: Partial<Record<RecruitTab, number>>;
  onExitRecruitMode?: () => void;
  recruitSort?: RecruitSort;
  onRecruitSort?: (sort: RecruitSort) => void;
  intl: IntlLike;
};

const ClientTabs = (props: ClientTabsProps) => {
  const { activeTab, onTab, counts, sort, onSort, intl, legacyState, onLegacyState, onRefresh, refreshing } = props;
  const { calendarOpen = false, onCalendar } = props;
  const { recruitMode = false, recruitTab = 'all', onRecruitTab, recruitCounts, onExitRecruitMode } = props;
  const { recruitSort = 'default', onRecruitSort } = props;
  const { recruitAvailable = false } = props;
  // V1 exposes the sort as a real <Select> (clientPage.js:2176-2177). The v46
  // control kept the chevron affordance but cycled the two options on click,
  // which reads as a dropdown and does not behave like one — so it is a proper
  // listbox now.
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement | null>(null);
  const tablistRef = useRef<HTMLDivElement | null>(null);

  // On a phone the chip row scrolls sideways, and the chip that was tapped can
  // end up scrolled out of sight — most visibly 提醒, the last chip, once the
  // list/calendar toggle claims part of the row. Bring the active one back into
  // view whenever it changes. `inline: 'nearest'` does nothing when it already
  // shows, and block 'nearest' keeps the page itself from jumping.
  useEffect(() => {
    const chip = tablistRef.current?.querySelector<HTMLElement>(
      recruitMode ? `[data-tab="recruit-${recruitTab}"]` : `[data-tab="${activeTab}"]`
    );
    chip?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [activeTab, recruitTab, recruitMode]);

  useEffect(() => {
    if (!sortOpen) return undefined;
    const onDocument = (event: MouseEvent) => {
      if (!sortRef.current?.contains(event.target as Node)) setSortOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSortOpen(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onEscape);
    };
  }, [sortOpen]);

  // V1's <select> carries one value. Mirror that: an active list state wins the
  // display, otherwise the control shows the sort that is in effect.
  const isActiveOption = (option: MenuOption): boolean => {
    if (option.kind === 'state') return legacyState === option.value;
    if (legacyState) return false;
    if (option.kind === 'sort') {
      // The default-order row yields to a menu tab: while 家庭 is the active
      // list, IT is what this single-valued control has to name, even though
      // the order underneath is still the server's.
      if (option.value === 'default') return sort === 'default' && !isMenuTab(activeTab);
      return option.value === sort;
    }
    // A tab row only counts as the current value while no sort is applied —
    // V1's single-valued <select> cannot show 「家庭」 and 「C.P.I Hi to Lo」 at
    // once, and picking a sort there replaces the list state.
    return option.value === activeTab && sort === 'default';
  };
  const visibleOptions = MENU_OPTIONS.filter(option => option.always || isActiveOption(option));
  // One value at a time, as V1's <select> has: the active list state, else the
  // active menu tab, else the sort. It falls back to the sort's own label for
  // the v46 tabs V1 has no equivalent of (跟進/潛力/家庭), where `default`
  // reads as 「All」 — the unsorted, server-ordered list.
  const activeOption = visibleOptions.find(isActiveOption);
  const triggerLabel = intl.formatMessage({ id: activeOption?.labelKey || SORT_LABEL[sort] });

  // No sort control on the 提醒 tab, as V1 had none there. Its order is fixed
  // (`sortClients` sorts by reminder date whatever the control says — pinned in
  // clientListView.test.ts), so the sort rows could not take effect, and the
  // calendar ignores list order and list state altogether. What was left was a
  // button naming 「按提醒日期」 that took the room the chips, the 列表/月曆
  // toggle and refresh need to share one line on a phone. The chosen sort is
  // only hidden, not cleared, so it resumes on the next tab; an active list
  // state is still named, and cleared, by the banner under this row.
  const showSort = activeTab !== 'reminder';

  /**
   * 招募模式 replaces the chip row outright rather than adding to it. The two
   * rows answer different questions — 全部 / 跟進 / 潛力 / 提醒 all ask 「show me
   * some of my 191 clients」, while these four ask 「where is each candidate」 —
   * and a row mixing them would make 全部 mean two different totals depending
   * on which chip was pressed last.
   *
   * The leading chip is the switch back, in the same seat the 招募 chip occupies
   * in business mode: the adviser came in through that corner of the row, so
   * that is where the hand goes to get out.
   *
   * Recruitment mode has its own dedicated sort & filter dropdown (新→舊, 評估分高→低,
   * 已做職涯測驗, 未評估) tailored for candidate pipeline management. Calendar
   * remains business-only. Both return untouched on the way back to 業務.
   */
  if (recruitMode && onRecruitTab && onExitRecruitMode) {
    const recruitTriggerLabel = intl.formatMessage({ id: RECRUIT_SORT_LABEL[recruitSort] });
    return (
      <div className="clientv2-tabs" data-testid="client-tabs" data-mode="recruit">
        <div className="clientv2-tablist" role="tablist" ref={tablistRef}>
          <button
            type="button"
            data-testid="client-tab-business"
            className="clientv2-tab is-recruit is-switch"
            onClick={onExitRecruitMode}
          >
            {intl.formatMessage({ id: 'clientv2-tab-business' })}
            <span className="clientv2-tab-meta">
              <span className="clientv2-tab-emoji clientv2-tab-icon" aria-hidden="true">
                📊
              </span>
              {/* Deliberately no count. 191 clients beside 20 candidates, on two
                  chips of the same row, reads as two slices of one total. */}
            </span>
          </button>
          {RECRUIT_TABS.map(tab => {
            const active = tab === recruitTab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid="client-tab"
                data-tab={`recruit-${tab}`}
                className={`clientv2-tab${active ? ' is-active' : ''}`}
                onClick={() => onRecruitTab(tab)}
              >
                {intl.formatMessage({ id: RECRUIT_TAB_LABEL[tab] })}
                <span className="clientv2-tab-meta">
                  {recruitCounts?.[tab] !== undefined && (
                    <span className="clientv2-tab-count">{recruitCounts[tab]}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div className="clientv2-tab-actions">
          {onRecruitSort && (
            <div className="clientv2-sort-wrap" ref={sortRef}>
              <button
                type="button"
                data-testid="client-sort"
                className="clientv2-sort"
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                title={recruitTriggerLabel}
                onClick={() => setSortOpen(open => !open)}
              >
                <ArrowUpDown size={14} aria-hidden="true" className="clientv2-sort-icon" />
                <span className="clientv2-sort-label">{recruitTriggerLabel}</span>
                <ChevronDown size={13} aria-hidden="true" className={sortOpen ? 'is-open' : ''} />
              </button>

              {sortOpen && (
                <ul
                  className="clientv2-sort-menu"
                  data-testid="client-sort-menu"
                  role="listbox"
                  aria-label={intl.formatMessage({ id: 'clientv2-recruit-sort-label' })}
                >
                  {RECRUIT_SORT_OPTIONS.map(option => {
                    const active = option.value === recruitSort;
                    return (
                      <li key={option.value}>
                        <button
                          type="button"
                          role="option"
                          data-testid="client-sort-option"
                          aria-selected={active}
                          className={`clientv2-sort-option${active ? ' is-active' : ''}`}
                          onClick={() => {
                            setSortOpen(false);
                            if (active) return;
                            onRecruitSort(option.value);
                          }}
                        >
                          {intl.formatMessage({ id: option.labelKey })}
                          {active && <Check size={13} aria-hidden="true" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {onRefresh && (
            <button
              type="button"
              data-testid="client-refresh"
              className={`clientv2-command-button is-icon${refreshing ? ' is-busy' : ''}`}
              aria-label={intl.formatMessage({ id: 'clientv2-command-refresh' })}
              title={intl.formatMessage({ id: 'clientv2-command-refresh' })}
              disabled={refreshing}
              onClick={onRefresh}
            >
              <RefreshCw size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="clientv2-tabs" data-testid="client-tabs">
      {/* The client-base indicator used to sit here, ahead of the tabs. It has
          moved up beside the search box (ClientCommandBar): it is a standing
          fact about the adviser's own book, not a control over this list, and
          on a phone it was taking a chunk of the row the tabs need. */}
      <div className="clientv2-tablist" role="tablist" ref={tablistRef}>
        {CLIENT_TAB_ROW.map(tab => {
          const count = counts[tab];
          const active = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid="client-tab"
              // Identity, so a caller (and a test) can address ONE chip without
              // counting from the left — the row's order is a display decision
              // and has changed once already.
              data-tab={tab}
              className={`clientv2-tab${active ? ' is-active' : ''}${tab === 'recruit' ? ' is-recruit' : ''}${
                tab === 'recruit' && recruitAvailable ? ' is-mode-switch' : ''
              }`}
              onClick={() => onTab(tab)}
            >
              {intl.formatMessage({ id: TAB_LABEL[tab] })}
              {/* Icon and count share one box. Wider than a phone the box is
                  `display: contents` and the icon is ordered back in front of
                  the label, so nothing changes there; on a phone the box is the
                  pill's second line, and the icon riding beside the count rather
                  than the label is what lets the whole tab row — toggle and
                  refresh included — fit on one line. */}
              <span className="clientv2-tab-meta">
                {/* An emoji like 招募's 💼, so both marked tabs carry colour
                    rather than one coloured glyph and one line icon. */}
                {tab === 'reminder' && (
                  <span className="clientv2-tab-emoji clientv2-tab-icon" aria-hidden="true">
                    🔔
                  </span>
                )}
                {/* The same 💼 the rows carry (ClientRow's row-recruit, V1's
                    ClientEntry.js:739-747), so the tab and the clients it lists
                    are marked with one glyph. Decorative here — the tab's own
                    「招募」 text already names it, so a second announcement of the
                    same thing would only slow a screen reader down. */}
                {tab === 'recruit' && (
                  <span className="clientv2-tab-emoji clientv2-tab-icon" aria-hidden="true">
                    💼
                  </span>
                )}
                {count !== undefined && <span className="clientv2-tab-count">{count}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="clientv2-tab-actions">
        {onCalendar && (
          <button
            type="button"
            data-testid="client-calendar-toggle"
            className={`clientv2-command-button clientv2-calendar-toggle${calendarOpen ? ' is-active' : ' is-icon'}`}
            aria-pressed={calendarOpen}
            aria-label={intl.formatMessage({ id: 'clientv2-view-calendar' })}
            // What a press will do: open the calendar, or go back to the list.
            title={intl.formatMessage({ id: calendarOpen ? 'clientv2-view-list' : 'clientv2-view-calendar' })}
            onClick={onCalendar}
          >
            {/* An icon alone did not read as "press again to leave" — while the
                calendar is open the button says so in words. */}
            {calendarOpen ? (
              <>
                <X size={14} aria-hidden="true" />
                <span>{intl.formatMessage({ id: 'clientv2-calendar-back' })}</span>
              </>
            ) : (
              <CalendarDays size={16} aria-hidden="true" />
            )}
          </button>
        )}
        {showSort && (
          <div className="clientv2-sort-wrap" ref={sortRef}>
            <button
              type="button"
              data-testid="client-sort"
              className="clientv2-sort"
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
              // The label is capped and can end in an ellipsis (see styles.scss);
              // the menu below always spells it out in full, and so does this.
              title={triggerLabel}
              onClick={() => setSortOpen(open => !open)}
            >
              <ArrowUpDown size={14} aria-hidden="true" className="clientv2-sort-icon" />
              <span className="clientv2-sort-label">{triggerLabel}</span>
              <ChevronDown size={13} aria-hidden="true" className={sortOpen ? 'is-open' : ''} />
            </button>

            {sortOpen && (
              <ul
                className="clientv2-sort-menu"
                data-testid="client-sort-menu"
                role="listbox"
                aria-label={intl.formatMessage({ id: 'clientv2-sort-label' })}
              >
                {visibleOptions.map(option => {
                  const active = isActiveOption(option);
                  return (
                    <li key={`${option.kind}-${option.value}`}>
                      <button
                        type="button"
                        role="option"
                        data-testid="client-sort-option"
                        aria-selected={active}
                        className={`clientv2-sort-option${active ? ' is-active' : ''}`}
                        onClick={() => {
                          setSortOpen(false);
                          if (active) return;
                          if (option.kind === 'sort') {
                            onSort(option.value);
                            // A menu-only tab has no chip to click, so the row
                            // that restores the default ORDER is also the way
                            // back out of it to the default LIST.
                            if (option.value === 'default' && isMenuTab(activeTab)) onTab('all');
                          } else if (option.kind === 'tab') {
                            // V1's "All" resets clientState; both tab entries are
                            // list states there, so they clear any active state
                            // AND any sort — clientState '' means "server order".
                            onLegacyState?.(null);
                            onSort('default');
                            onTab(option.value);
                          } else onLegacyState?.(option.value);
                        }}
                      >
                        {intl.formatMessage({ id: option.labelKey })}
                        {active && <Check size={13} aria-hidden="true" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* The page does not refetch the whole list on entry — every mutation
            refreshes its own client, and the CPI bands are recomputed locally.
            This is the deliberate way to pull everything again (a colleague's
            edits, another device), instead of paying for it on every visit. */}
        <button
          type="button"
          data-testid="client-refresh"
          className={`clientv2-command-button is-icon${refreshing ? ' is-refreshing' : ''}`}
          aria-label={intl.formatMessage({ id: 'clientv2-command-refresh' })}
          title={intl.formatMessage({ id: 'clientv2-command-refresh' })}
          disabled={refreshing}
          onClick={onRefresh}
        >
          <RefreshCw size={16} aria-hidden="true" />
        </button>

        {/* 新增客戶 is a floating action button now (see ClientAddFab), where
            V1 keeps it — it is reachable from anywhere in a long list instead
            of only from the top of the page, and the tab row gets its width
            back on mobile. */}
      </div>
    </div>
  );
};

export default injectIntl(ClientTabs);
